/** Full-width alert banner for connection status, stale data, and system warnings. */

interface AlertBannerProps {
  /** Banner variant controls color and icon */
  variant: 'info' | 'warning' | 'error'
  /** Message to display */
  message: string
  /** Optional action button text */
  actionLabel?: string
  /** Callback when action button clicked */
  onAction?: () => void
  /** Optional dismiss callback — shows X button */
  onDismiss?: () => void
}

const variantStyles = {
  info: 'bg-blue-900/60 border-blue-700 text-blue-200',
  warning: 'bg-amber-900/60 border-amber-700 text-amber-200',
  error: 'bg-red-900/60 border-red-700 text-red-200',
}

const iconMap = {
  info: '\u2139\uFE0F',
  warning: '\u26A0\uFE0F',
  error: '\u{1F534}',
}

export function AlertBanner({
  variant,
  message,
  actionLabel,
  onAction,
  onDismiss,
}: AlertBannerProps) {
  return (
    <div
      className={`flex items-center gap-3 border-b px-4 py-2 text-sm ${variantStyles[variant]}`}
      role="alert"
      data-testid="alert-banner"
    >
      <span aria-hidden="true">{iconMap[variant]}</span>
      <span className="flex-1">{message}</span>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="rounded px-2 py-0.5 text-xs font-medium underline hover:no-underline"
        >
          {actionLabel}
        </button>
      )}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="ml-2 rounded p-0.5 hover:bg-white/10"
          aria-label="Dismiss"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}
